import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import {
  Container, Typography, Box, Button, Divider, IconButton, TextField, CircularProgress
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import EditIcon from '@mui/icons-material/Edit';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { useAuth } from '../../../hooks/useAuth';
import Tooltip from '@mui/material/Tooltip';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';

const FreeBoardDetail = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [likeLoading, setLikeLoading] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentForm, setCommentForm] = useState({ author_name: '', content: '' });
  const [commentError, setCommentError] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  const { user, isLoggedIn } = useAuth();
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ title: '', content: '', password: '' });
  const [editError, setEditError] = useState('');
  const [editLoading, setEditLoading] = useState(false);

  // 게시글 불러오기
  const fetchPost = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('free_posts')
      .select('*')
      .eq('id', postId)
      .single();
    setPost(data);
    setLoading(false);
  };

  // 댓글 불러오기
  const fetchComments = async () => {
    const { data } = await supabase
      .from('free_comments')
      .select('*')
      .eq('post_id', postId)
      .order('created_at', { ascending: true });
    setComments(data || []);
  };

  useEffect(() => {
    fetchPost();
    fetchComments();
    // 조회수 증가
    supabase.rpc('increment_post_views', { post_id: postId });
    // (increment_post_views는 Supabase Function으로, 없으면 생략)
    // eslint-disable-next-line
  }, [postId]);

  // 좋아요
  const handleLike = async () => {
    if (!post) return;
    setLikeLoading(true);
    await supabase
      .from('free_posts')
      .update({ likes: (post.likes || 0) + 1 })
      .eq('id', post.id);
    fetchPost();
    setLikeLoading(false);
  };

  // 댓글 작성
  const handleComment = async () => {
    setCommentError('');
    if (!commentForm.author_name.trim() || !commentForm.content.trim()) {
      setCommentError('작성자명과 내용을 입력하세요.');
      return;
    }
    setCommentLoading(true);
    const { error } = await supabase.from('free_comments').insert({
      post_id: postId,
      author_name: commentForm.author_name,
      content: commentForm.content
    });
    setCommentLoading(false);
    if (error) {
      setCommentError('댓글 작성에 실패했습니다.');
    } else {
      setCommentForm({ author_name: '', content: '' });
      fetchComments();
    }
  };

  // 수정 버튼 클릭 시
  const handleEditOpen = () => {
    setEditForm({ title: post.title, content: post.content, password: '' });
    setEditError('');
    setEditOpen(true);
  };

  // 수정 저장
  const handleEditSave = async () => {
    setEditError('');
    if (!editForm.title.trim() || !editForm.content.trim()) {
      setEditError('제목과 내용을 입력하세요.');
      return;
    }
    // 비로그인 글은 비밀번호 확인
    if (post.is_guest && post.password && editForm.password !== post.password) {
      setEditError('비밀번호가 일치하지 않습니다.');
      return;
    }
    setEditLoading(true);
    const { error } = await supabase
      .from('free_posts')
      .update({ title: editForm.title, content: editForm.content })
      .eq('id', post.id);
    setEditLoading(false);
    if (error) {
      setEditError('수정에 실패했습니다.');
    } else {
      setEditOpen(false);
      fetchPost();
    }
  };

  // 본인 글 여부
  const isMyPost = post && (isLoggedIn
    ? (post.user_id && user && post.user_id === user.id)
    : (post.is_guest && post.password));

  // 비로그인 글이면서 비밀번호가 없는 경우 수정 불가 안내
  const isGuestNoPassword = post && !isLoggedIn && post.is_guest && !post.password;

  if (loading) return <Container sx={{ py: 8 }}><CircularProgress /></Container>;
  if (!post) return (
    <Container sx={{ py: 8 }}>
      <Typography variant="h6">존재하지 않는 게시글입니다.</Typography>
      <Button startIcon={<ArrowBackIcon />} sx={{ mt: 2 }} onClick={() => navigate(-1)}>
        목록으로 돌아가기
      </Button>
    </Container>
  );

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
        목록으로 돌아가기
      </Button>
      <Box>
        <Typography variant="h5" fontWeight={700}>{post.title}</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 1, mb: 1 }}>
          <Typography variant="body2" color="text.secondary">{post.author_name || '익명'}</Typography>
          <Typography variant="body2" color="text.secondary">{new Date(post.created_at).toLocaleString()}</Typography>
          <Typography variant="body2" color="text.secondary">조회수 {post.views || 0}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton size="small" onClick={handleLike} disabled={likeLoading}>
              {likeLoading ? <ThumbUpAltIcon color="primary" /> : <ThumbUpAltOutlinedIcon />}
            </IconButton>
            <Typography variant="body2">{post.likes || 0}</Typography>
          </Box>
          <Tooltip
            title={isGuestNoPassword ? '비밀번호가 없어 수정할 수 없습니다.' : '게시글 수정'}
            arrow
          >
            <span>
              <Button
                variant="outlined"
                size="small"
                startIcon={<EditIcon />}
                sx={{ ml: 2 }}
                onClick={handleEditOpen}
                disabled={isGuestNoPassword || !isMyPost}
              >
                수정
              </Button>
            </span>
          </Tooltip>
        </Box>
        {isGuestNoPassword && (
          <Typography color="warning.main" variant="body2" sx={{ mb: 1 }}>
            비로그인으로 작성된 이 글은 비밀번호가 없어 수정할 수 없습니다.
          </Typography>
        )}
        <Divider sx={{ my: 2 }} />
        <Box className="free-board-content" sx={{ overflowX: 'auto', minHeight: 120 }}>
          <ReactMarkdown rehypePlugins={[rehypeRaw]}>{post.content}</ReactMarkdown>
        </Box>
      </Box>
      {/* 수정 다이얼로그 */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>게시글 수정</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="제목"
            value={editForm.title}
            onChange={e => setEditForm({ ...editForm, title: e.target.value })}
            fullWidth
            required
          />
          <TextField
            label="내용"
            value={editForm.content}
            onChange={e => setEditForm({ ...editForm, content: e.target.value })}
            fullWidth
            multiline
            minRows={5}
            required
          />
          {post.is_guest && post.password && (
            <TextField
              label="비밀번호"
              type="password"
              value={editForm.password}
              onChange={e => setEditForm({ ...editForm, password: e.target.value })}
              fullWidth
              required
            />
          )}
          {editError && (
            <Typography color="error" variant="body2">{editError}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>취소</Button>
          <Button variant="contained" onClick={handleEditSave} disabled={editLoading}>저장</Button>
        </DialogActions>
      </Dialog>
      <Divider sx={{ my: 4 }} />
      {/* 댓글 */}
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>댓글</Typography>
        {comments.length === 0 && (
          <Typography color="text.secondary" sx={{ mb: 2 }}>아직 댓글이 없습니다.</Typography>
        )}
        {comments.map(c => (
          <Box key={c.id} sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 2 }}>
            <Typography variant="subtitle2">{c.author_name || '익명'}</Typography>
            <Typography variant="body2" color="text.secondary">{new Date(c.created_at).toLocaleString()}</Typography>
            <Typography variant="body1" sx={{ mt: 1 }}>{c.content}</Typography>
          </Box>
        ))}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
          <TextField
            label="작성자명"
            value={commentForm.author_name}
            onChange={e => setCommentForm({ ...commentForm, author_name: e.target.value })}
            size="small"
            sx={{ maxWidth: 240 }}
          />
          <TextField
            label="댓글 내용"
            value={commentForm.content}
            onChange={e => setCommentForm({ ...commentForm, content: e.target.value })}
            multiline
            minRows={2}
          />
          {commentError && <Typography color="error" variant="body2">{commentError}</Typography>}
          <Button variant="contained" onClick={handleComment} disabled={commentLoading} sx={{ alignSelf: 'flex-end', mt: 1 }}>
            댓글 작성
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default FreeBoardDetail; 